import React, { useEffect, useState } from "react";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, StatusPill } from "@/components/Primitives";
import DataTable from "@/components/DataTable";

export default function Staffing() {
  const [staff, setStaff] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ name: "", role: "RN", credentials: "", expiration_date: "", email: "" });
  const [taskForm, setTaskForm] = useState({ title: "", assigned_to: "", due_date: "", status: "pending" });

  const load = () => {
    api.get("/staff").then(r=>setStaff(r.data));
    api.get("/tasks").then(r=>setTasks(r.data));
  };
  useEffect(load, []);

  const addStaff = async (e) => {
    e.preventDefault();
    await api.post("/staff", form);
    toast.success("Staff added");
    setForm({ name: "", role: "RN", credentials: "", expiration_date: "", email: "" });
    load();
  };

  const addTask = async (e) => {
    e.preventDefault();
    await api.post("/tasks", taskForm);
    toast.success("Task assigned");
    setTaskForm({ title: "", assigned_to: "", due_date: "", status: "pending" });
    load();
  };

  const updateTask = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status });
    load();
  };

  const staffCols = [
    { key: "name", label: "Name" },
    { key: "role", label: "Role" },
    { key: "credentials", label: "Credential" },
    { key: "expiration_date", label: "Expires", render: (r)=>new Date(r.expiration_date).toLocaleDateString() },
    { key: "days_until_expiration", label: "Days", render: (r)=>(
      <span className={r.alert?"text-red-600 font-semibold":"text-brand-text"}>
        {r.days_until_expiration ?? "—"} {r.alert && <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />}
      </span>
    )},
  ];

  const taskCols = [
    { key: "title", label: "Task" },
    { key: "assigned_to", label: "Assignee" },
    { key: "due_date", label: "Due", render: (r)=>new Date(r.due_date).toLocaleDateString() },
    { key: "status", label: "Status", render: (r)=><StatusPill status={r.status} /> },
    { key: "actions", label: "Update", sortable: false, render: (r) => (
      <select value={r.status} onChange={(e)=>updateTask(r.id, e.target.value)} className="text-xs border border-brand rounded">
        <option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
      </select>
    )},
  ];

  const onboarding = ["W-2 & I-9 forms","HIPAA training","BLS certification","EHR system access","Badge issued","Org chart review","Schedule shadowing"];

  return (
    <>
      <PageHeader eyebrow="Module 6" title="Staffing AI Copilot"
        subtitle="Credential expiration alerts, task assignment, and onboarding checklists." testid="staffing-page" />

      <SectionCard title="Staff Directory" testid="staff-section">
        <DataTable data={staff} columns={staffCols} searchKeys={["name","role","credentials"]} testid="staff-table" />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <SectionCard title="Add Staff" testid="add-staff">
          <form onSubmit={addStaff} className="space-y-2">
            <input placeholder="Name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="staff-name" />
            <select value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              <option>RN</option><option>LPN</option><option>MA</option><option>Biller</option><option>Admin</option>
            </select>
            <input placeholder="Credential" value={form.credentials} onChange={(e)=>setForm({...form, credentials:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            <input type="date" value={form.expiration_date?.split("T")[0]||""} onChange={(e)=>setForm({...form, expiration_date: e.target.value+"T00:00:00Z"})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="staff-expiration" />
            <button type="submit" className="w-full bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B]">
              <Plus className="w-4 h-4 inline mr-1" /> Add
            </button>
          </form>
        </SectionCard>

        <div className="lg:col-span-2">
          <SectionCard title="Task Board" testid="task-board"
            actions={
              <form onSubmit={addTask} className="flex gap-2 items-center">
                <input placeholder="Task" value={taskForm.title} onChange={(e)=>setTaskForm({...taskForm, title:e.target.value})} required
                  className="px-2 py-1 border border-brand rounded text-xs" />
                <input placeholder="Assignee" value={taskForm.assigned_to} onChange={(e)=>setTaskForm({...taskForm, assigned_to:e.target.value})} required
                  className="px-2 py-1 border border-brand rounded text-xs" />
                <input type="date" value={taskForm.due_date?.split("T")[0]||""} onChange={(e)=>setTaskForm({...taskForm, due_date: e.target.value+"T00:00:00Z"})} required
                  className="px-2 py-1 border border-brand rounded text-xs" />
                <button className="px-2 py-1 bg-brand-teal text-white rounded text-xs">Add</button>
              </form>
            }>
            <DataTable data={tasks} columns={taskCols} searchKeys={["title","assigned_to"]} testid="task-table" />
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Onboarding Checklist" testid="onboarding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {onboarding.map((o,i) => (
            <label key={i} className="flex items-center gap-2 text-sm p-2 hover:bg-brand-bg rounded">
              <input type="checkbox" className="accent-[#4A0E8F]" /> {o}
            </label>
          ))}
        </div>
      </SectionCard>
    </>
  );
}
